import companyService from '@planning/services/companyService'
import dayjs from 'dayjs'
import { action, computed, makeObservable, observable } from 'mobx'
import {
  ContainerJourneyReportDataStore,
  ContainerJourneyReportDto,
} from './ContainerJourneyReportDataStore'

export type ContainerJourneyReportColumnDto = ContainerJourneyReportDto & {
  services: ContainerJourneyReportDto['services']
  hasServices: boolean
  customerName?: string
}

export class ContainerJourneyReportViewStore {
  from?: Date = dayjs().subtract(30, 'day').toDate()
  to?: Date = dayjs().toDate()
  vesselVisitId?: number

  isLoading = false
  hasFetched = false
  companiesMap = new Map<number, string>()

  constructor(private dataStore: ContainerJourneyReportDataStore) {
    makeObservable(this, {
      from: observable,
      to: observable,
      vesselVisitId: observable,
      isLoading: observable,
      hasFetched: observable,
      companiesMap: observable,

      reportData: computed,

      setFrom: action,
      setTo: action,
      setVesselVisitId: action,
      setCompaniesMap: action,
      reset: action,
    })
  }

  get reportData(): ContainerJourneyReportColumnDto[] {
    return this.dataStore.journeyData
      .filter(journey => journey.entryDate && journey.exitDate)
      .map(journey => {
        const customerName = journey.customerId
          ? this.companiesMap.get(journey.customerId)
          : undefined

        return {
          ...journey,
          customerName,
          hasServices: !!journey.services.length,
        }
      })
  }

  fetch = async () => {
    if (!this.from || !this.to) return

    try {
      this.setIsLoading(true)
      await this.dataStore.fetch(this.from, this.to, this.vesselVisitId)
      await this.loadCustomerNames()
      this.setHasFetched(true)
    } finally {
      this.setIsLoading(false)
    }
  }

  private loadCustomerNames = async () => {
    const allCustomerIds = this.dataStore.journeyData
      .map(journey => journey.customerId)
      .filter((id): id is number => id != null)

    const uniqueCustomerIds = [...new Set(allCustomerIds)]

    if (uniqueCustomerIds.length === 0) {
      this.setCompaniesMap(new Map())
      return
    }

    try {
      const companiesInfo = await companyService.getByReferenceIds(uniqueCustomerIds)
      const dictionary = new Map<number, string>()

      companiesInfo.forEach(company => {
        if (company.referenceId != null) {
          dictionary.set(company.referenceId, company.shortName || company.name)
        }
      })

      this.setCompaniesMap(dictionary)
    } catch (error) {
      console.error('Failed to fetch company names:', error)
      this.setCompaniesMap(new Map())
    }
  }

  setCompaniesMap = (map: Map<number, string>) => {
    this.companiesMap = map
  }

  setIsLoading = (value: boolean) => {
    this.isLoading = value
  }

  setHasFetched = (value: boolean) => {
    this.hasFetched = value
  }

  setFrom = (from: Date) => {
    this.from = from
  }

  setTo = (to: Date) => {
    this.to = to
  }

  setVesselVisitId = (vesselVisitId?: number) => {
    this.vesselVisitId = vesselVisitId
  }

  reset = () => {
    this.from = dayjs().subtract(30, 'day').toDate()
    this.to = dayjs().toDate()
    this.vesselVisitId = undefined
    this.isLoading = false
    this.hasFetched = false
    this.companiesMap.clear()
  }
}
